import type { JSX } from 'react'
import { IconGlyph } from '../icons/registry'

/**
 * One option, unframed.
 *
 * A tile is a box with a drawing in it — the right shape for "pick a shape",
 * the wrong one for "pick a thing you have to see". On a screen of
 * photographs the border and the fill are most of the ink on the page, and
 * they are competing with the only thing worth looking at.
 *
 * So: the picture at the full width of its column, the word under it, and
 * nothing else. Selection is the word turning primary and a hairline arriving
 * under the picture, never a box lighting up — and it is on a data attribute
 * as well, because a state carried only in a hex value is a state half the
 * room cannot see.
 *
 * This knows nothing about where the picture came from. The caller resolves
 * that; the plate is handed a source or handed none, and the empty case draws
 * at the same size in the same place, so a screen the catalogue can illustrate
 * twice out of eight has no holes in it.
 */
export interface OptionPlateProps {
  readonly label: string
  readonly caption?: string | undefined
  /** Icon name from the step document — what is drawn when there is no photo. */
  readonly icon?: string | undefined
  /**
   * The photograph, when there is one. Decorative on purpose: it illustrates
   * the label, and the label is what she is choosing and what she hears.
   */
  readonly imageSrc?: string | undefined
  readonly selected: boolean
  /** Where the idea came from, when it came from an earlier screen. */
  readonly suggestionLabel?: string | undefined
  /** Single-select steps are radios; the rest are toggles. */
  readonly single: boolean
  /** An effect-free option on a screen that has others: the way past. */
  readonly optOut?: boolean
  readonly disabled?: boolean
  readonly onSelect: () => void
}

/** Big enough to be the subject of its own frame when it is standing in for one. */
const GLYPH_SIZE = 72

export function OptionPlate({
  label,
  caption,
  icon,
  imageSrc,
  selected,
  suggestionLabel,
  single,
  optOut = false,
  disabled = false,
  onSelect,
}: OptionPlateProps): JSX.Element {
  return (
    <button
      type="button"
      className="fr-plate"
      role={single ? 'radio' : undefined}
      aria-checked={single ? selected : undefined}
      aria-pressed={single ? undefined : selected}
      data-selected={selected}
      data-optout={optOut ? true : undefined}
      disabled={disabled}
      onClick={onSelect}
    >
      <span className="fr-plate-frame">
        {imageSrc ? (
          <img src={imageSrc} alt="" loading="lazy" draggable={false} />
        ) : (
          <IconGlyph name={icon} label={label} size={GLYPH_SIZE} />
        )}
      </span>
      <span className="fr-plate-label fr-display text-[1.125rem] leading-tight">{label}</span>
      {caption ? (
        <span className="text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
          {caption}
        </span>
      ) : null}
      {suggestionLabel ? (
        // Where the idea came from, not what is chosen: it stays when she takes
        // the plate off. Tracking is drawn in because the word has half a
        // phone's width to fit in.
        <span className="fr-eyebrow" style={{ color: 'var(--fr-primary)', letterSpacing: '0.12em' }}>
          {suggestionLabel}
        </span>
      ) : null}
    </button>
  )
}
